import { useEffect, useRef, useState, type FormEvent } from 'react';
import { ApiError } from '../../lib/apiClient';
import { getErrorMessage } from '../../lib/errors';
import type { FeedSourceRead } from '../../types/api';
import { FeedSourceRow } from './FeedSourceRow';
import { sourceLabel } from './feedSourceLabels';
import type { FeedSourceCollection } from './newsApi';

/** The panel's own wording for the two failures this endpoint can return. */
function addErrorMessage(caught: unknown): string {
  if (caught instanceof ApiError) {
    if (caught.code === 'FEED_INVALID') {
      return 'We could not read a feed or a news page on that site. Try its news or blog page.';
    }
    if (caught.code === 'FEED_UNREACHABLE') {
      return 'That site did not let us read it. Some publishers block feed readers; try its RSS link directly, or a different page.';
    }
    if (caught.code === 'FEED_EXISTS') {
      return 'You already follow that feed.';
    }
  }
  return getErrorMessage(caught, 'That source could not be added.');
}

interface NewsAddSourceProps {
  /**
   * Owned by `NewsPage`, not by this panel. The filter select, the sources
   * section and this field are one collection: a source added here has to
   * appear in both of the others without a refetch, which three mounted
   * `useCollection`s could not do.
   */
  collection: FeedSourceCollection;
  /** True while this is the dialog's open tab. Puts the caret in the field. */
  isActive: boolean;
  /** Reported so the page knows the feed's inputs moved while the dialog was open. */
  onAdded: () => void;
  /**
   * Fired once an unfollow has really gone through — after the undo window, not
   * when the row left the screen. The headlines are read out of these rows, so
   * this is the page re-pulling them, and it fires whether or not this dialog
   * is still open: the deferred delete outlives it.
   */
  onRemoved: () => void;
}

/**
 * Add your own — paste any website and we find its feed.
 *
 * The first tab of the feed dialog, and the plainest of the three: one address,
 * one button. The backend finds the site's feed and falls back to reading the
 * page itself when there is none, so "paste anything" is the honest instruction.
 *
 * Underneath sits what is already followed, because "you already follow that
 * feed" is a better answer given before the user pastes than after. Every one of
 * those rows can be unfollowed from here too: the list of what is being read and
 * the control for stopping are the same list. The delete is the collection's own
 * undo-window one, so it is the same delete as under the headlines — the row
 * goes now and the DELETE fires when the toast closes.
 *
 * Nothing here calls a model or spends a generation.
 */
export function NewsAddSource({ collection, isActive, onAdded, onRemoved }: NewsAddSourceProps) {
  const [feedUrl, setFeedUrl] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [writeError, setWriteError] = useState<string | null>(null);
  const [addedLabel, setAddedLabel] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isActive) inputRef.current?.focus();
  }, [isActive]);

  async function handleAdd(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmed = feedUrl.trim();
    setAddedLabel(null);
    if (!trimmed) {
      setWriteError('Paste a feed address first.');
      return;
    }
    if (!/^https?:\/\/\S+$/i.test(trimmed)) {
      setWriteError('A feed address starts with http:// or https://.');
      return;
    }

    setIsSaving(true);
    setWriteError(null);
    try {
      const created = await collection.create({ feed_url: trimmed });
      setFeedUrl('');
      setAddedLabel(created.title?.trim() || trimmed);
      onAdded();
    } catch (caught) {
      setWriteError(addErrorMessage(caught));
    } finally {
      setIsSaving(false);
    }
  }

  /** Optimistic: the row goes now, the DELETE waits out the undo window. */
  function startUnfollow(item: FeedSourceRead) {
    setAddedLabel(null);
    collection.removeWithUndo({
      id: item.id,
      label: sourceLabel(item),
      failureMessage: 'That source could not be removed.',
      onRemoved,
    });
  }

  return (
    <div className="news-add">
      <form className="feed-add" onSubmit={(event) => void handleAdd(event)} noValidate>
        <label className="feed-add__field">
          <span className="news-add__label">Website or feed address</span>
          <input
            className="field__input feed-add__input"
            type="url"
            placeholder="https://example.com/blog"
            value={feedUrl}
            onChange={(event) => setFeedUrl(event.target.value)}
            ref={inputRef}
          />
        </label>
        <button className="button button--primary" type="submit" disabled={isSaving}>
          {isSaving ? 'Adding' : 'Add source'}
        </button>
      </form>

      <p className="field__hint">
        Paste any website. We look for its feed and read the page itself when there is none.
      </p>

      {writeError ? (
        <p className="form__error" role="alert">
          {writeError}
        </p>
      ) : null}

      {collection.deleteError ? (
        <p className="form__error" role="alert">
          {collection.deleteError}
        </p>
      ) : null}

      {addedLabel && !writeError ? (
        <p className="news-add__receipt" role="status">
          Following {addedLabel}. It joins the next update — hit Refresh to see it now.
        </p>
      ) : null}

      <div className="feed-following">
        <h3 className="feed-following__title">Following</h3>
        {collection.isLoading ? (
          <p className="kb-section__status" role="status">
            Loading your sources
          </p>
        ) : collection.items.length === 0 ? (
          <p className="feed-following__empty">
            Nothing yet. Paste a site you trust — a trade publication, a blog — and the newsfeed
            starts reading it.
          </p>
        ) : (
          // Every source, whatever its status, newest first: the list endpoint
          // orders it and the collection keeps that order, so there is nothing
          // to sort here and nothing held back. Long lists scroll in place.
          <ul className="feed-following__list">
            {collection.items.map((item) => (
              <FeedSourceRow
                key={item.id}
                source={item}
                badge="status"
                action={
                  <button
                    className="button button--ghost button--danger-text feed-following__unfollow"
                    type="button"
                    aria-label={`Unfollow ${sourceLabel(item)}`}
                    onClick={() => startUnfollow(item)}
                  >
                    Unfollow
                  </button>
                }
              />
            ))}
          </ul>
        )}
        <p className="field__hint">
          Unfollowing stops the newsfeed reading a source. You can undo it while the toast is up.
        </p>
      </div>
    </div>
  );
}
